import { useQuery } from "@tanstack/react-query";
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { useAuth } from "./useAuth";
import type { Course } from "./useCourses";
import type { ProductScheduleTemplate } from "@/types/schedule";
import { TRIP_STATUS } from "@/lib/trip-status";

export interface CancellationDetails {
  cancellation_id: string;
  booking_id: number;
  cancelled_by: string;
  cancel_reason: string;
  cancel_fee: number;
  refund_amount: number;
  original_amount: number;
  toss_payment_key: string | null;
  toss_cancel_data: unknown;
  cancelled_at: string;
  created_at: string;
  updated_at: string;
}

export interface BookingWithDetails {
  booking_id: number;
  order_id: string;
  user_id: string;
  trip_id: number;
  total_price: number;
  status: string;
  payment_key: string | null;
  created_at: string;
  updated_at: string;
  // 취소 관련 필드 (booking_cancellations 조인 시 사용)
  cancellation?: CancellationDetails[];
  trip: {
    trip_id: number;
    product_id: number;
    departure_date: string;
    departure_point: string | null;
    arrival_point: string | null;
    via_points: string | null;
    return_dep_point: string | null;
    return_via_points: string | null;
    return_arr_point: string | null;
    bus_number: string | null;
    bus_type: string | null;
    driver_name: string | null;
    driver_phone: string | null;
    bus_guide: string | null;
    tour_leader: string | null;
    status: string;
    min_participants: number;
    max_participants: number;
    confirmation_deadline: string | null;
    product: {
      product_id: number;
      name: string;
      description: string | null;
      price: number;
      max_participants?: number;
      schedule_template?: ProductScheduleTemplate | null;
      product_courses: Array<{
        course_id: number;
        course: Course;
      }>;
    };
  };
  booking_participants: Array<{
    id: string;
    booking_id: number;
    name: string;
    phone: string | null;
    email: string | null;
    seat: number | null;
    participant_order: number;
    created_at: string;
  }>;
}

export const useBookingDetail = (bookingId: number) => {
  const { user, isAuthenticated } = useAuth();
  const { supabase } = useSupabaseConnection();

  return useQuery({
    queryKey: ['booking-detail', bookingId, user?.id],
    queryFn: async (): Promise<BookingWithDetails> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          trip:trips (
            *,
            product:products (
              *,
              schedule_template,
              product_courses (
                course_id,
                course:courses (
                  course_id, name, difficulty, start_point, end_point,
                  main_photo_url, estimated_time, length, description
                )
              )
            )
          ),
          booking_participants (*),
          cancellation:booking_cancellations (*)
        `)
        .eq('booking_id', bookingId)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data as unknown as BookingWithDetails;
    },
    enabled: isAuthenticated && !!bookingId,
  });
};

// 취소 가능 여부 판단 함수
export const canCancelBooking = (booking: BookingWithDetails): { canCancel: boolean; reason?: string } => {
  if (booking.status === 'cancelled') {
    return { canCancel: false, reason: '이미 취소된 예약입니다' };
  }
  
  if (booking.trip?.status === TRIP_STATUS.COMPLETED) {
    return { canCancel: false, reason: '완료된 여행은 취소할 수 없습니다' };
  }

  const departureDate = new Date(booking.trip.departure_date);
  const now = new Date();
  const hoursUntilDeparture = (departureDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  // 출발 시간이 지났으면 취소 불가
  if (hoursUntilDeparture <= 0) {
    return { canCancel: false, reason: '출발 시간이 지난 예약은 취소할 수 없습니다' };
  }

  // 당일까지는 취소 가능 (환불 금액은 다를 수 있음)
  // 환불 정책:
  // - 4일 전까지: 100% 환불
  // - 3~1일 전 오전: 70% 환불
  // - 1일 전 오후~당일: 0% 환불 (취소는 가능)
  return { canCancel: true };
};

// 수정 가능 여부 판단 함수
export const canModifyBooking = (booking: BookingWithDetails): { canModify: boolean; reason?: string; hoursUntilDeparture: number } => {
  const departureDate = new Date(booking.trip.departure_date);
  const now = new Date();
  const hoursUntilDeparture = (departureDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  if (booking.status !== 'confirmed') {
    return { canModify: false, reason: '확정된 예약만 수정 가능합니다', hoursUntilDeparture };
  }
  
  if (hoursUntilDeparture <= 24) {
    return { canModify: false, reason: '출발 24시간 전까지만 수정 가능합니다', hoursUntilDeparture };
  }

  return { canModify: true, hoursUntilDeparture };
};

// 취소 내역 조회 훅
export const useCancellationDetails = (bookingId: number) => {
  const { user, isAuthenticated } = useAuth();
  const { supabase } = useSupabaseConnection();

  return useQuery({
    queryKey: ['cancellation-details', bookingId, user?.id],
    queryFn: async (): Promise<CancellationDetails | null> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('booking_cancellations')
        .select('*')
        .eq('booking_id', bookingId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: isAuthenticated && !!bookingId,
  });
};